// src/services/familyService.ts
import { AxiosResponse } from 'axios'
import api from './api'

export interface FamilyMember {
  id: string
  _id: string
  firstName: string
  lastName: string
  relationship: string
  dateOfBirth: string
  email: string
  phone: string
  contactEmail?: string
  contactPhone?: string
  accessLevel: {
    viewAppointments: boolean
    bookAppointments: boolean
    viewBilling: boolean
    receiveNotifications: boolean
  }
  profilePhotoUrl?: string
  isContactPersion?: boolean
}

export interface FamilyMemberFormData {
  firstName: string
  lastName: string
  relationship: string
  dateOfBirth: Date | null
  email: string
  phone: string
  contactEmail: string
  contactPhone: string
  profilePhotoUrl?: string
  accessLevel: {
    viewAppointments: boolean
    bookAppointments: boolean
    viewBilling: boolean
    receiveNotifications: boolean
  }
}

export interface FamilyMembersResponse {
  data: FamilyMember[]
  total: number
  page: number
  limit: number
}

export interface FamilyLoginResponse {
  familyMember: {
    [x: string]: string
    avatar?: null
    id: string
    _id: string
    firstName: string
    lastName: string
    email: string
    role: string
  }
  access_token: string
  refresh_token: string
}

// Get family members
export const getFamilyMembers = async (
  search?: string,
  page: number = 1,
  limit: number = 10
): Promise<FamilyMembersResponse> => {
  const response: AxiosResponse<FamilyMembersResponse> = await api.get(
    '/patient/family',
    {
      params: { search, page, limit },
    }
  )
  return response.data
}

// Add family member
export const addFamilyMember = async (
  memberData: Omit<FamilyMember, 'id' | '_id'>
): Promise<FamilyMember> => {
  const response: AxiosResponse<FamilyMember> = await api.post(
    '/patient/family',
    memberData
  )
  return response.data
}

// Update family member
export const updateFamilyMember = async (
  memberId: string,
  updateData: Partial<FamilyMember>
): Promise<FamilyMember> => {
  const response: AxiosResponse<FamilyMember> = await api.put(
    `/patient/family/${memberId}`,
    updateData
  )
  return response.data
}

// Delete family member
export const deleteFamilyMember = async (memberId: string): Promise<void> => {
  await api.delete(`/patient/family/${memberId}`)
}

// Login as family member - Updated to match your patient login response structure
export const loginAsFamilyMember = async (
  memberId: string
): Promise<FamilyLoginResponse> => {
  const response: AxiosResponse<FamilyLoginResponse> = await api.post(
    `/patient/family/${memberId}/login`
  )
  return response.data
}

// Set family member as emergency contact
export const setEmergencyContact = async (
  memberId: string
): Promise<FamilyMember> => {
  const response: AxiosResponse<FamilyMember> = await api.patch(
    `/patient/family/${memberId}/set-contact-person`
  )
  return response.data
}
